import styled from "@emotion/styled";
import CloseIcon from "@mui/icons-material/Close";
import {
  Box,
  Dialog,
  DialogContent,
  DialogTitle,
  Divider,
  IconButton,
  Typography,
} from "@mui/material";
import { FullScreenLoader } from "components/loader/FullScreenLoader";
import { CustomerProviderName, federatedLogin } from "hooks/authentication";
import type React from "react";
import { useState } from "react";
import { STORAGE_KEYS, sessionStorageService } from "service/storage";
import LoginPopupBackground from "../../assets/imgs/loginPopupBackground.png";

const LoginButton = styled.button({
  height: "38.8px",
  width: "275px",
  padding: "6.4px 15px",
  marginBottom: "16px",
  borderRadius: "2px",
  border: "1px solid #009FE4",
  backgroundColor: "#009FE4",
  color: "#fff",
  justifyContent: "center",
  alignItems: "center",
  boxShadow: "0px 2px 0px 0px rgba(0, 0, 0, 0.04)",
  cursor: "pointer",
});

const RegisterButton = styled.button({
  height: "38.8px",
  width: "112px",
  padding: "6.4px 15px",
  marginBottom: "16px",
  borderRadius: "2px",
  border: "none",
  backgroundColor: "#fff",
  color: "#009FE4",
  justifyContent: "center",
  alignItems: "center",
  fontFamily: 'var(--font-family, "AktivGrotesk")',
  fontWeight: 700,
  cursor: "pointer",
});

interface UnauthenticationDialogProps {
  isOpen: boolean;
  onClose: (value: boolean) => void;
  reason?: "session-expired" | "unauthenticated";
}

/**
 * Login / register entry dialog shown when the user is unauthenticated or
 * their session has expired.
 *
 * A {@link FullScreenLoader} is shown between the moment this dialog closes
 * and the moment the next Gigya screen finishes loading — `gigya.showScreenSet`
 * fires `onAfterScreenLoad` / `onError` / `onHide` asynchronously, so without
 * this cover the user sees an unresponsive blank page (CX-2734).
 *
 * @param isOpen  Whether the dialog is visible.
 * @param onClose Called with `false` when the dialog should close.
 * @param reason  Drives the title copy: `"session-expired"` vs `"unauthenticated"`.
 */
export const UnauthenticationDialog = ({
  isOpen = false,
  onClose,
  reason = "unauthenticated",
}: UnauthenticationDialogProps) => {
  const [isLoading, setIsLoading] = useState(false);

  const handleLoginEmployees = (e: React.MouseEvent<HTMLButtonElement>) => {
    e.stopPropagation();
    onClose(false);
    setIsLoading(true);
    federatedLogin(CustomerProviderName.CovestroAzureSSO).catch(() => {
      setIsLoading(false);
    });
  };

  const handleLoginCDC = (e: React.MouseEvent<HTMLButtonElement>) => {
    e.stopPropagation();
    onClose(false);
    sessionStorageService.removeItem(STORAGE_KEYS.RELOADED);
    setIsLoading(true);
    (window.gigya?.accounts as any)?.showScreenSet({
      screenSet: "Default-RegistrationLogin",
      startScreen: "cov_login",
      onAfterScreenLoad: () => {
        setIsLoading(false);
      },
      onError: () => {
        setIsLoading(false);
      },
      onHide: () => {
        setIsLoading(false);
      },
      // CX-3151 follow-up: the JWT fetch used to be acquired here from
      // `onAfterSubmit`, but that hook fires before Gigya's session exists.
      // It is now triggered from Gigya's global `onLogin` handler instead
      // (see `onCdcLogin` in CovestroHeader.tsx).
    });
  };
  const handleRegister = (e: React.MouseEvent<HTMLButtonElement>) => {
    e.stopPropagation();
    onClose(false);
    sessionStorageService.removeItem(STORAGE_KEYS.RELOADED);
    setIsLoading(true);
    (window.gigya?.accounts as any)?.showScreenSet({
      screenSet: "Default-RegistrationLogin",
      startScreen: "cov_registration_imagio_cf",
      onAfterScreenLoad: () => {
        setIsLoading(false);
      },
      onError: () => {
        setIsLoading(false);
      },
      onHide: () => {
        setIsLoading(false);
      },
      // CX-3151 follow-up: the JWT fetch used to be acquired here from
      // `onAfterSubmit` when the registration screenset ended on the login
      // screen, but that hook fires before Gigya's session exists. It is now
      // triggered from Gigya's global `onLogin` handler instead (see
      // `onCdcLogin` in CovestroHeader.tsx).
    });
  };

  return (
    <>
      <FullScreenLoader open={isLoading} />
      <Dialog
        id="UnAuthenDialog"
        data-cy={"UnAuthenDialog"}
        // The Gigya login/TFA screenset renders outside this dialog's DOM
        // (into document.body), so MUI's default focus trap would steal focus
        // back and make the screenset inputs (e.g. the TFA code field)
        // impossible to type into. Let focus escape to the Gigya screen.
        disableEnforceFocus
        sx={{ "& .MuiPaper-root": { width: "466px", height: "397px" } }}
        onClose={() => onClose(false)}
        aria-labelledby="customized-dialog-title"
        open={isOpen}
      >
        <DialogTitle
          sx={{
            m: 0,
            p: 2,
            height: "174px",
            position: "relative",
            backgroundImage: `url(${LoginPopupBackground})`,
            backgroundPosition: "center center",
            backgroundRepeat: "no-repeat",
            backgroundSize: "cover",
          }}
          id="customized-dialog-title"
        >
          <Box
            sx={{
              width: "100%",
              height: "100%",
              position: "absolute",
              top: 0,
              left: 0,
            }}
          >
            <Typography
              sx={{
                width: "272px",
                height: "64px",
                fontFamily: 'var(--font-family, "AktivGrotesk")',
                fontSize: "var(--heading-h3-24-regular-size, 24px)",
                color: "#fff",
                fontWeight: 700,
                lineHeight: "32px",
                margin: "auto",
                marginTop: "64px",
                textAlign: "center",
              }}
              variant="h2"
              component={"div"}
            >
              {reason === "session-expired"
                ? "Your session has expired. Please log in again."
                : "Please log in to access your search results"}
            </Typography>
          </Box>
        </DialogTitle>
        <IconButton
          aria-label="close"
          onClick={() => onClose(false)}
          sx={(_theme) => ({
            position: "absolute",
            right: 8,
            top: 8,
            color: "#fff",
          })}
        >
          <CloseIcon />
        </IconButton>
        <DialogContent dividers sx={{ overflow: "hidden" }}>
          <Box sx={{ textAlign: "center" }}>
            <LoginButton onClick={handleLoginCDC}>
              Login for Customers
            </LoginButton>
            <LoginButton onClick={handleLoginEmployees}>
              Login for Covestro Employees
            </LoginButton>
            <Divider sx={{ width: "275px", margin: "auto" }}>
              <Typography variant={"overline"}>OR</Typography>
            </Divider>
            <RegisterButton onClick={handleRegister}>Register</RegisterButton>
          </Box>
        </DialogContent>
      </Dialog>
    </>
  );
};
